import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { User, Mail, Phone, Building2, MapPin, Globe, Trash2, Edit3, Home, FileText, Key, MessageSquare } from 'lucide-react';
import { Map, AdvancedMarker } from '@vis.gl/react-google-maps';
import { useTranslation } from '../../context/LanguageContext';
import { getUserContacts, setContactRating, removeUserContact } from '../../services/userContactsService';
import { getPeopleByUuid } from '../../services/apiService';
import { StarRating } from '../ui';
import ConfirmModal from '../ui/ConfirmModal';
import { EditContactModal } from '../contacts';

/**
 * Contact Detail Content Component
 * Displays private contact information (from localStorage)
 * Similar layout to PeopleDetailContent but for private contacts
 */
const ContactDetailContent = () => {
    const { localuuid } = useParams();
    const navigate = useNavigate();
    const { t, language } = useTranslation();

    const [contact, setContact] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [deleteModal, setDeleteModal] = useState(false);
    const [defaultMapCenter, setDefaultMapCenter] = useState(null);

    // Get user info
    const userInfo = JSON.parse(localStorage.getItem('cabaza_user') || '{}');
    const userId = userInfo.id;
    const userUuid = userInfo.peopleUuid;

    useEffect(() => {
        loadContact();
        loadDefaultMapCenter();
    }, [localuuid, userId]);

    // Load default map center: GPS if accepted, otherwise user's profile coordinates
    const loadDefaultMapCenter = async () => {
        // First try GPS
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) => {
                    setDefaultMapCenter({
                        lat: position.coords.latitude,
                        lng: position.coords.longitude
                    });
                },
                async () => {
                    // GPS denied/failed - use user's profile coordinates
                    await loadUserCoordinates();
                },
                { enableHighAccuracy: true, timeout: 5000 }
            );
        } else {
            // No geolocation support - use user's profile coordinates
            await loadUserCoordinates();
        }
    };

    const loadUserCoordinates = async () => {
        if (!userUuid) return;
        try {
            const userData = await getPeopleByUuid(userUuid);
            if (userData) {
                const lat = userData.latitude || userData.LATITUDE;
                const lng = userData.longitude || userData.LONGITUDE;
                if (lat && lng) {
                    setDefaultMapCenter({ lat: parseFloat(lat), lng: parseFloat(lng) });
                }
            }
        } catch (err) {
            console.error('Error loading user coordinates:', err);
        }
    };

    const loadContact = async () => {
        if (!localuuid || !userId) {
            setError('Contact not found');
            setLoading(false);
            return;
        }

        try {
            setLoading(true);
            const contacts = await getUserContacts(userId);
            const foundContact = contacts.find(c => c.localuuid === localuuid);

            if (foundContact) {
                setContact(foundContact);
            } else {
                setError('Contact not found');
            }
        } catch (err) {
            console.error('Error loading contact:', err);
            setError('Error loading contact');
        } finally {
            setLoading(false);
        }
    };

    const handleRatingChange = async (newRating) => {
        if (!contact) return;
        try {
            await setContactRating(contact.id, newRating, userId);
            setContact({ ...contact, rating: newRating });
        } catch (err) {
            console.error('Error updating rating:', err);
        }
    };

    const handleDelete = async () => {
        if (!contact) return;
        try {
            await removeUserContact(userId, contact.id);
            navigate('/');
        } catch (err) {
            console.error('Error deleting contact:', err);
        }
        setDeleteModal(false);
    };

    const handleContactUpdated = (updatedContact) => {
        setContact({ ...contact, ...updatedContact });
        setEditModalOpen(false);
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cabaza-purple"></div>
            </div>
        );
    }

    if (error || !contact) {
        return (
            <div className="p-6">
                <div className="text-center py-12">
                    <User className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <p className="text-gray-500">{error || 'Contact not found'}</p>
                </div>
            </div>
        );
    }

    const fullName = `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || 'Unknown';
    const hasLocation = contact.latitude && contact.longitude;

    return (
        <div className="p-6 w-full">
            {/* Header Card */}
            <div className="bg-gray-50 border-2 border-gray-300 rounded-2xl p-6 mb-6 relative">
                {/* Badge Contact */}
                <div className="absolute top-4 right-4">
                    <span className="bg-gray-400 text-white px-3 py-1 rounded-full text-sm font-bold">
                        {t.badges?.contact || 'CONTACT'}
                    </span>
                </div>

                {/* Actions */}
                <div className="absolute top-4 left-4 flex gap-2">
                    <button
                        onClick={() => setEditModalOpen(true)}
                        className="p-2 bg-cabaza-purple text-white rounded-full hover:bg-cabaza-pink transition-colors"
                        title={t.profile?.edit || 'Edit'}
                    >
                        <Edit3 className="w-4 h-4" />
                    </button>
                    <button
                        onClick={() => setDeleteModal(true)}
                        className="p-2 bg-red-500 text-white rounded-full hover:bg-red-600 transition-colors"
                        title={t.dashboard?.remove || 'Delete'}
                    >
                        <Trash2 className="w-4 h-4" />
                    </button>
                </div>

                {/* Profile Info */}
                <div className="flex flex-col md:flex-row items-center md:items-start gap-6 mt-12">
                    {/* Avatar */}
                    <div className="w-24 h-24 rounded-full bg-gray-200 flex items-center justify-center flex-shrink-0">
                        <User className="w-12 h-12 text-gray-400" />
                    </div>

                    {/* Info */}
                    <div className="flex-1 text-center md:text-left">
                        <h1 className="text-2xl font-bold text-gray-700 mb-2">{fullName}</h1>

                        {contact.position && (
                            <div className="flex items-center justify-center md:justify-start text-gray-600 mb-1">
                                <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                                <span>{contact.position}</span>
                            </div>
                        )}

                        {contact.company && (
                            <div className="flex items-center justify-center md:justify-start text-gray-600 mb-1">
                                <Building2 className="w-4 h-4 mr-2 text-gray-400" />
                                <span className="font-medium">{contact.company}</span>
                            </div>
                        )}

                        {/* Rating */}
                        <div className="mt-4">
                            <StarRating
                                rating={contact.rating || 0}
                                onRatingChange={handleRatingChange}
                                size="md"
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Contact Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Left Column - Basic Info */}
                <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <User className="w-5 h-5 mr-2 text-cabaza-purple" />
                        {t.profile?.contactInfo || 'Contact Information'}
                    </h2>

                    <div className="space-y-4">
                        {contact.email && (
                            <div className="flex items-start">
                                <Mail className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.email || 'Email'}</p>
                                    <a href={`mailto:${contact.email}`} className="text-cabaza-purple hover:underline">
                                        {contact.email}
                                    </a>
                                </div>
                            </div>
                        )}

                        {contact.phone && (
                            <div className="flex items-start">
                                <Phone className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.phone || 'Phone'}</p>
                                    <a href={`tel:${contact.phone}`} className="text-cabaza-purple hover:underline">
                                        {contact.phone}
                                    </a>
                                </div>
                            </div>
                        )}

                        {contact.gsm && contact.gsm !== contact.phone && (
                            <div className="flex items-start">
                                <Phone className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.mobile || 'Mobile'}</p>
                                    <a href={`tel:${contact.gsm}`} className="text-cabaza-purple hover:underline">
                                        {contact.gsm}
                                    </a>
                                </div>
                            </div>
                        )}

                        {contact.website && (
                            <div className="flex items-start">
                                <Globe className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.website || 'Website'}</p>
                                    <a
                                        href={contact.website.startsWith('http') ? contact.website : `https://${contact.website}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-cabaza-purple hover:underline"
                                    >
                                        {contact.website}
                                    </a>
                                </div>
                            </div>
                        )}

                        {(contact.googleAddress || contact.address) && (
                            <div className="flex items-start">
                                <MapPin className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.address || 'Address'}</p>
                                    <p className="text-gray-700">{contact.googleAddress || contact.address}</p>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column - Additional Info */}
                <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <FileText className="w-5 h-5 mr-2 text-cabaza-purple" />
                        {t.profile?.additionalInfo || 'Additional Information'}
                    </h2>

                    <div className="space-y-4">
                        {contact.accessCode && (
                            <div className="flex items-start">
                                <Key className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.accessCode || 'Access Code'}</p>
                                    <p className="text-gray-700 font-mono">{contact.accessCode}</p>
                                </div>
                            </div>
                        )}

                        {contact.homeNeeds && contact.homeNeeds.length > 0 && (
                            <div className="flex items-start">
                                <Home className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.homeNeeds || 'Home Needs'}</p>
                                    <div className="flex flex-wrap gap-1 mt-1">
                                        {contact.homeNeeds.map((need, index) => (
                                            <span key={index} className="bg-gray-100 text-gray-700 px-2 py-0.5 rounded text-sm">
                                                {need}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        )}

                        {contact.notes && (
                            <div className="flex items-start">
                                <MessageSquare className="w-5 h-5 mr-3 text-cabaza-purple flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-sm text-gray-500">{t.form?.notes || 'Notes'}</p>
                                    <p className="text-gray-700 whitespace-pre-wrap">{contact.notes}</p>
                                </div>
                            </div>
                        )}

                        {!contact.accessCode && (!contact.homeNeeds || contact.homeNeeds.length === 0) && !contact.notes && (
                            <p className="text-gray-400 italic">
                                {t.profile?.noAdditionalInfo || 'No additional information'}
                            </p>
                        )}
                    </div>
                </div>
            </div>

            {/* Map */}
            {(hasLocation || defaultMapCenter) && (
                <div className="mt-6 bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                        <MapPin className="w-5 h-5 mr-2 text-cabaza-purple" />
                        {t.profile?.location || 'Location'}
                    </h2>
                    <div className="h-64 rounded-lg overflow-hidden">
                        <Map
                            defaultZoom={hasLocation ? 15 : 12}
                            defaultCenter={hasLocation
                                ? { lat: contact.latitude, lng: contact.longitude }
                                : defaultMapCenter
                            }
                            mapId="contact-detail-map"
                            disableDefaultUI={true}
                            zoomControl={true}
                        >
                            {hasLocation && (
                                <AdvancedMarker position={{ lat: contact.latitude, lng: contact.longitude }}>
                                    <div className="bg-gray-500 text-white p-2 rounded-full shadow-lg">
                                        <MapPin className="w-5 h-5" />
                                    </div>
                                </AdvancedMarker>
                            )}
                        </Map>
                    </div>
                    {!hasLocation && (
                        <p className="text-sm text-gray-500 mt-2 italic">
                            {t.profile?.noLocationSet || 'No location set for this contact'}
                        </p>
                    )}
                </div>
            )}

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteModal}
                onClose={() => setDeleteModal(false)}
                onConfirm={handleDelete}
                title={t.dashboard?.confirmDelete || 'Delete Contact'}
                variant="danger"
            >
                <p className="text-gray-600">
                    {t.dashboard?.confirmDeleteMessage || 'Are you sure you want to delete this contact?'}
                </p>
                <p className="font-semibold mt-2">{fullName}</p>
            </ConfirmModal>

            {/* Edit Contact Modal */}
            {editModalOpen && contact && (
                <EditContactModal
                    isOpen={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    contact={contact}
                    onContactUpdated={handleContactUpdated}
                    onContactDeleted={() => navigate('/')}
                />
            )}
        </div>
    );
};

export default ContactDetailContent;
